import type { BookingPreferences, ScreeningExclusion, SelectedSlot } from "./bookingFlowMachine"
import { bookingFlowMachine } from "./bookingFlowMachine"
import { BookedAppointment, ScreeningProgram, useBookingContext } from "./hooks"
import { BookedPage } from "./pages/BookedPage"
import { ConsentPage } from "./pages/ConsentPage"
import { HealthQuestionsPage } from "./pages/HealthQuestionsPage"
import { NoAvailabilityPage } from "./pages/NoAvailabilityPage"
import { NotEligiblePage } from "./pages/NotEligiblePage"
import { SelectPreferencesPage } from "./pages/SelectPreferencesPage"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { useMachine } from "@xstate/react"
import React from "react"

type BookingFlowProps = {
  // The program the member is booking into — drives the screening question set.
  program: ScreeningProgram
  // Navigation is the host's concern — these come in as callbacks rather than the
  // package importing the router.
  onExit?: () => void
  onReturnToProviderResults?: () => void
}

// Loads the flow's initial-load bundle (getBookingContext) before the machine starts: the consent
// state (so the `alreadyConsented` guard has a real value at init) and the member's legal name
// (shown on the confirmation screen).
export function BookingFlow({ program, onExit, onReturnToProviderResults }: BookingFlowProps): JSX.Element {
  const bookingContext = useBookingContext()

  if (bookingContext.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  // On a fetch failure, don't block booking — treat as not-yet-signed (show the consent page) and
  // leave the name off the confirmation screen.
  const alreadySignedConsent = bookingContext.data?.consent.hasSignedAll ?? false
  const member = bookingContext.data?.member
  const memberName = member ? [member.firstName, member.lastName].filter(Boolean).join(" ") || undefined : undefined

  return (
    <BookingMachine
      program={program}
      memberName={memberName}
      alreadySignedConsent={alreadySignedConsent}
      onExit={onExit}
      onReturnToProviderResults={onReturnToProviderResults}
    />
  )
}

function BookingMachine({
  program,
  memberName,
  alreadySignedConsent,
  onExit,
  onReturnToProviderResults
}: BookingFlowProps & { alreadySignedConsent: boolean; memberName?: string }): JSX.Element | null {
  const [state, send] = useMachine(bookingFlowMachine, { input: { alreadySignedConsent, program } })
  const onContinue = () => send({ type: "CONTINUE" })
  const onDisqualified = (exclusion: ScreeningExclusion) => send({ type: "DISQUALIFIED", exclusion })
  const onConfirmBooking = (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) =>
    send({ type: "CONFIRM_BOOKING", preferences, slot, appointment })

  if (state.matches("consent"))
    return <ConsentPage onContinue={onContinue} onReturnToProviderResults={onReturnToProviderResults} />
  if (state.matches("healthQuestions"))
    return (
      <HealthQuestionsPage
        program={program}
        onContinue={onContinue}
        onDisqualified={onDisqualified}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches({ seeAvailability: "preferences" }))
    return (
      <SelectPreferencesPage
        program={program}
        onConfirmBooking={onConfirmBooking}
        onReturnToProviderResults={onReturnToProviderResults}
      />
    )
  if (state.matches("booked"))
    return (
      <BookedPage
        program={program}
        appointment={state.context.appointment}
        appointmentTypeId={state.context.preferences?.appointmentTypeId}
        memberName={memberName}
        onBackToHome={onExit}
      />
    )
  if (state.matches("notEligible")) return <NotEligiblePage exclusion={state.context.exclusion} onBackToHome={onExit} />
  if (state.matches("noAvailability")) return <NoAvailabilityPage onBackToHome={onExit} />
  return null
}
